<template>
    <div class="my-4">
        <div class="flex items-center justify-between">
            <label
                class="mb-1 block text-sm font-medium"
                :for="name"
                >{{ $t(label) }}
                <span
                    v-if="required === true"
                    class="text-danger-500"
                    >*</span
                >
            </label>
            <Tooltip
                class="ml-2"
                bg="dark"
                size="md"
                position="left"
            >
                <div class="text-sm text-gray-200">{{ $t(help) }}</div>
            </Tooltip>
        </div>
        <div class="flex items-center justify-between">
            <div>
                <a
                    v-if="modelValue?.url && !modelValue?.delete"
                    :href="modelValue.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary-additional-500 text-sm underline"
                >
                    {{ modelValue.file_name || modelValue.name }}
                </a>
                <span
                    v-else-if="uploading"
                    class="text-sm text-gray-400"
                    >{{ $t('uploading') }}</span
                >
                <span
                    v-else
                    class="text-sm text-gray-400"
                    >{{ $t('no_document_uploaded') }}</span
                >
            </div>
            <div>
                <div class="m-2 text-right">
                    <button
                        class="btn-sm bg-primary-additional-500 hover:bg-primary-additional-600 text-white disabled:cursor-not-allowed disabled:opacity-50"
                        type="button"
                        :disabled="uploading"
                        @click="openFileBrowser"
                    >
                        {{ $t('choose_file') }}
                    </button>
                </div>
                <div class="m-2 text-right">
                    <button
                        class="btn-sm bg-danger-500 hover:bg-danger-600 text-white disabled:cursor-not-allowed disabled:opacity-50"
                        type="button"
                        :disabled="uploading"
                        @click="clear"
                    >
                        {{ $t('clear') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { Tooltip } from '@src/components'
import { ImageInterface, stdImage } from '@src/types'
import { uploadPublic } from '@src/utils/api/s3'
import { useToastStore } from '@src/utils/stores/toast'
import { useFileDialog, useVModels } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    label: string
    name: string
    help: string
    required?: boolean
    modelValue: ImageInterface | null
    accept?: string
}>()

const emit = defineEmits(['update:modelValue'])

const { modelValue } = useVModels(props, emit)

const i18n = useI18n()
const toast = useToastStore()

const accept = props.accept ?? '.pdf'
const uploading = ref(false)

const { open, files, reset } = useFileDialog({
    multiple: false,
    accept: accept,
})

function init(): void {
    modelValue.value = (modelValue.value ?? new stdImage()) as ImageInterface
}

function openFileBrowser(): void {
    // useFileDialog keeps the previous selection on its file input. Without resetting it
    // first, picking the same file again fires no change event, the watcher below never
    // runs and the button looks dead - which is exactly what happens after a failed try.
    reset()
    open()
}

function clear(): void {
    reset()
    if (modelValue.value) {
        modelValue.value.delete = true
        modelValue.value.upload_key = null
        modelValue.value.url = null
        modelValue.value.file_name = null
        modelValue.value.name = null
    }
}

/**
 * Mirrors the html accept attribute: a comma separated list of file extensions
 * (.pdf), mime types (application/pdf) or mime groups (image/*).
 */
function isAcceptedFile(file: File): boolean {
    const patterns = accept
        .split(',')
        .map((pattern) => pattern.trim().toLowerCase())
        .filter((pattern) => pattern.length > 0)

    if (!patterns.length) {
        return true
    }

    const fileName = file.name.toLowerCase()
    const mimeType = file.type.toLowerCase()

    return patterns.some((pattern) => {
        if (pattern.startsWith('.')) {
            return fileName.endsWith(pattern)
        }
        if (pattern.endsWith('/*')) {
            return mimeType.startsWith(pattern.slice(0, -1))
        }
        return mimeType === pattern
    })
}

watch(files, async function (files) {
    if (!files) {
        return
    }

    const file = files?.item(0)

    if (!file) {
        return
    }

    if (!isAcceptedFile(file)) {
        toast.error(
            i18n.t('toast_error_document_upload_invalid_type', {
                accepted: accept,
            })
        )
        reset()
        return
    }

    uploading.value = true

    try {
        const uploadResponse = await uploadPublic(file)

        if (modelValue.value) {
            modelValue.value.file_name = file.name
            modelValue.value.name = file.name
            modelValue.value.upload_key = uploadResponse.data.key
            modelValue.value.url = uploadResponse.data.temp_storage_url

            if (modelValue.value.delete) {
                delete modelValue.value.delete
            }
        }
    } catch (error) {
        // The file is put directly on s3 with a pre-signed url, so a failing upload never
        // passes the api error interceptor and would otherwise go by unnoticed.
        console.error(error)
        toast.error(
            i18n.t('toast_error_document_upload_failed', { file: file.name })
        )
        reset()
    } finally {
        uploading.value = false
    }
})

onMounted(() => {
    init()
})
</script>
